// Google Business Profile post options, dashboard side.
//
// The server (autopilot-backend/gbp-post.js) is the authority: it validates
// every gbp_settings write and refuses a bad one with a sentence. The checks
// here only exist so the Post Generator can stop BEFORE a paid generation
// rather than after it, and so the person sees what is missing next to the
// field. They mirror the server's required-field rules and nothing more.

export const GBP_TOPIC_OPTIONS = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'OFFER',    label: 'Offer' },
  { value: 'EVENT',    label: 'Event' },
];

// Google's six buttons. GET_OFFER is deprecated and ALERT posts are never
// offered, matching the server.
export const GBP_CTA_OPTIONS = [
  { value: '',           label: 'No button' },
  { value: 'BOOK',       label: 'Book' },
  { value: 'ORDER',      label: 'Order' },
  { value: 'SHOP',       label: 'Shop' },
  { value: 'LEARN_MORE', label: 'Learn more' },
  { value: 'SIGN_UP',    label: 'Sign up' },
  { value: 'CALL',       label: 'Call' },
];

export const GBP_WEEKDAYS = [
  ['MONDAY', 'Mon'], ['TUESDAY', 'Tue'], ['WEDNESDAY', 'Wed'], ['THURSDAY', 'Thu'],
  ['FRIDAY', 'Fri'], ['SATURDAY', 'Sat'], ['SUNDAY', 'Sun'],
];

export const GBP_WEEK_OCCURRENCES = [
  ['FIRST', 'First'], ['SECOND', 'Second'], ['THIRD', 'Third'], ['FOURTH', 'Fourth'], ['LAST', 'Last'],
];

export const GBP_VIDEO_NOTE = 'Google doesn’t take videos — this post will skip Google.';

export function defaultGbpSettings() {
  return {
    topic_type: 'STANDARD',
    cta: { action_type: '', url: '' },
    offer: { coupon_code: '', terms: '', redeem_url: '' },
    event: { title: '', start_date: '', start_time: '', end_date: '', end_time: '' },
    recurrence: null,
  };
}

// Fill in anything a stored value is missing, so the form never reads
// undefined.x. A null stored value means Standard with no button.
export function editableGbpSettings(stored) {
  const d = defaultGbpSettings();
  if (!stored) return d;
  return {
    ...d,
    ...stored,
    cta:   { ...d.cta,   ...(stored.cta   || {}) },
    offer: { ...d.offer, ...(stored.offer || {}) },
    event: { ...d.event, ...(stored.event || {}) },
    recurrence: stored.recurrence ? { ...stored.recurrence } : null,
  };
}

const isHttpUrl = (s) => {
  try { const u = new URL(String(s || '').trim()); return u.protocol === 'https:' || u.protocol === 'http:'; }
  catch { return false; }
};

// The first thing that would make the server refuse these settings, as a
// sentence, or null when they are fine. Same order as the form.
export function gbpSettingsProblem(s) {
  if (!s) return null;
  const type = s.topic_type || 'STANDARD';
  const label = type === 'OFFER' ? 'Offer' : 'Event';
  if (type === 'EVENT' || type === 'OFFER') {
    const ev = s.event || {};
    if (!String(ev.title || '').trim()) return `Google: ${label} title is required.`;
    if (!ev.start_date) return `Google: ${label} start date is required.`;
    if (!ev.end_date)   return `Google: ${label} end date is required.`;
    const start = `${ev.start_date}T${ev.start_time || '00:00'}`;
    const end   = `${ev.end_date}T${ev.end_time || '23:59'}`;
    if (end <= start) return `Google: ${label} must end after it starts.`;
    // Google requires a stop date for any repeat — confirmed live Oct 4,
    // although its docs call the field optional. Same rule as the server.
    if (s.recurrence?.pattern) {
      if (!s.recurrence.series_end_date) return 'Google: pick a date to stop repeating — Google needs an end date for a repeating post.';
      if (s.recurrence.series_end_date < ev.start_date) return 'Google: the repeat must stop after the first date.';
    }
  }
  const action = s.cta?.action_type;
  if (action && action !== 'CALL' && !isHttpUrl(s.cta.url)) {
    return 'Google: the button needs a web address starting with https://';
  }
  if (type === 'OFFER' && s.offer?.redeem_url && !isHttpUrl(s.offer.redeem_url)) {
    return 'Google: the redeem link needs a web address starting with https://';
  }
  return null;
}

// What is stored: blank optional fields dropped so the row stays readable.
// The server normalises again; this just avoids sending '' where null is meant.
export function gbpSettingsForSave(s) {
  if (!s) return null;
  const type = s.topic_type || 'STANDARD';
  const out = { topic_type: type };
  if (s.cta?.action_type) out.cta = s.cta.action_type === 'CALL' ? { action_type: 'CALL' } : { action_type: s.cta.action_type, url: s.cta.url };
  if (type === 'EVENT' || type === 'OFFER') {
    const ev = s.event || {};
    out.event = { title: ev.title, start_date: ev.start_date, end_date: ev.end_date };
    if (ev.start_time) out.event.start_time = ev.start_time;
    if (ev.end_time)   out.event.end_time = ev.end_time;
    if (s.recurrence?.pattern) out.recurrence = s.recurrence;
  }
  if (type === 'OFFER') out.offer = { ...s.offer };
  if (s.summary) out.summary = s.summary;
  return out;
}

// The note a post card shows when Google will not (or did not) get the post.
export function gbpNoteFor(post) {
  if (!post) return null;
  const platforms = Array.isArray(post.platforms) ? post.platforms : [];
  if (!platforms.includes('google_business')) return null;
  if ((post.media_type || 'image') === 'video') return GBP_VIDEO_NOTE;
  return null;
}

// Adds 'google_business' to a platform list when the picker's gb flag is on.
export function withGoogle(list, picker) {
  return picker && picker.gb ? [...list, 'google_business'] : list;
}

// ── The 1,500-character trim ────────────────────────────────────────────────
//
// The server sends Google at most GBP_TEXT_MAX characters (gbp-post.js
// gbpSummaryFor: hashtags stripped first, then cut to 1,499 + "…"). Google's
// API states no limit; 1,500 is what Google's own app enforces, and a 1,550-
// character caption published fine on Oct 4 because we trimmed it. The trim
// was silent, so the dashboard now says so wherever the Google options show.
export const GBP_TEXT_MAX = 1500;
export const GBP_TRIM_NOTE = 'Google will show the first 1,500 characters';

// Same rule as the server's stripHashtags (gbp-post.js): a # followed by a
// word with a letter in it; "#1" stays.
export function stripHashtagsForGoogle(text) {
  return String(text || '')
    .replace(/(^|[^\p{L}\p{N}_&])#[\p{L}\p{N}_]*\p{L}[\p{L}\p{N}_]*/gu, '$1')
    .split('\n').map(l => l.replace(/[ \t]{2,}/g, ' ').trimEnd()).join('\n')
    .replace(/\n{3,}/g, '\n\n').trim();
}

// The trim note for this text going to Google, or null when it fits.
export function gbpTrimNote(text) {
  return stripHashtagsForGoogle(text).length > GBP_TEXT_MAX ? GBP_TRIM_NOTE : null;
}
